import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'components/ui/drawer';
import DeclareUnavailabilityForm from './DeclareUnavailabilityForm';

/**
 * The profile screen's declaration sheet — a bottom sheet and not a second screen. The ticket
 * calls it a "feuille de saisie", it is the native idiom on a phone, and it saves a route, a
 * second back button and an entry in `App.js`'s technician allow-list. The mock-up draws a full
 * screen; the divergence is written down in the lot's plan.
 *
 * Nothing but the container: the form lives in `DeclareUnavailabilityForm`, which the ticket
 * screen's own sheet shows as its second view.
 */

interface DeclareUnavailabilitySheetProps {
  open: boolean;
  onClose: () => void;
}

const DeclareUnavailabilitySheet = ({ open, onClose }: DeclareUnavailabilitySheetProps) => {
  const { t } = useTranslation();

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      {/*
        Three panes, and the middle one is the only one that scrolls.
        `overflow-y-auto` on `DrawerContent` itself is inert: vaul stamps it with
        `[data-vaul-drawer] { touch-action: none }` and owns every touch on it, so on a 360×640 dp
        phone the sheet simply clipped at the WebView's bottom edge and the actions below the fold
        were unreachable. `touch-action` is not inherited, so a plain child scrolls normally — and
        the actions sit outside it, which is what keeps them on screen at any height.
        `mx-auto sm:max-w-lg` because `DrawerContent` is `inset-x-0`: full width is right for a
        phone and absurd on a desktop, where the form then spans the whole window.
      */}
      <DrawerContent className="mx-auto max-h-[92dvh] w-full overflow-hidden sm:max-w-lg">
        <DrawerHeader className="shrink-0 text-left">
          <DrawerTitle>{t('Declare an unavailability')}</DrawerTitle>
          <DrawerDescription>{t('A period without a new intervention')}</DrawerDescription>
        </DrawerHeader>

        <DeclareUnavailabilityForm
          open={open}
          onClose={onClose}
          doneLabel={t('Back to my profile')}
          onDone={onClose}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default DeclareUnavailabilitySheet;
